<script setup lang="ts">
import { computed } from 'vue'
import MultiChoice from './questionTypes/MultiChoice.vue'
import SingleChoice from './questionTypes/SingleChoice.vue'
import TextField from './questionTypes/TextField.vue'
import NumberInput from './questionTypes/NumberInput.vue'
import SliderDisplay from './questionTypes/SliderDisplay.vue'

import type { SurveyQuestion } from '@/types/surveyTypes'
import { useSurvey, AnswerTarget } from '@/composables/onlineSurvey/useSurvey'

const { shouldShowQuestion } = useSurvey()

const props = defineProps<{
  question: SurveyQuestion
  answerTarget?: AnswerTarget // Optional prop with default
}>()

const showQuestion = computed(() => shouldShowQuestion(props.question))
</script>

<template>
  <div v-if="showQuestion" class="question-wrapper py-6">
    <div class="question-header mb-5">
      <h3 class="question-label is-size-4-desktop is-size-5-mobile">
        {{ props.question.label }}
        <span v-if="props.question.required" class="required-mark" title="Erforderlich">*</span>
      </h3>
    </div>

    <div class="question-content">
      <MultiChoice
        v-if="props.question.type === 'multi_choice'"
        :question="props.question"
        :answerTarget="props.answerTarget"
      />
      <SingleChoice
        v-else-if="props.question.type === 'single_choice'"
        :question="props.question"
        :answerTarget="props.answerTarget"
      />
      <TextField
        v-else-if="props.question.type === 'text'"
        :question="props.question"
        :answerTarget="props.answerTarget"
      />
      <NumberInput
        v-else-if="props.question.type === 'number'"
        :question="props.question"
        :answerTarget="props.answerTarget"
      />
      <SliderDisplay
        v-else-if="props.question.type === 'slider'"
        :question="props.question"
        :answerTarget="props.answerTarget"
      />
    </div>

    <!-- Divider for separation (except for the last one if managed by parent, but here we add it to all) -->
    <div class="question-divider mt-6"></div>
  </div>
</template>

<style scoped>
.question-wrapper {
  position: relative;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
}

.question-header {
  text-align: center;
}

.question-label {
  color: var(--primary-color);
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 0.5rem;
}

.required-mark {
  color: var(#ff3860);
  margin-left: 0.25rem;
  font-size: 1.25em;
  vertical-align: middle;
}

.question-content {
  width: 100%;
}

.question-divider {
  height: 2px;
  background: linear-gradient(
    to right,
    transparent,
    var(--bulma-border-weak, #f5f5f5),
    transparent
  );
  width: 60%;
  margin: 0 auto;
}

/* Letzter Divider im Step entfernen */
.question-wrapper:last-of-type .question-divider {
  display: none;
}
</style>
